// src/app/_landing/components/DashboardHeader.tsx
"use client";

import { Infinity, Trophy, Info, Hammer } from "lucide-react";
import { Button } from "@heroui/react";
import { useRouter } from "next/navigation";
import { ChallengeUser } from "@/types/challenges.types";

interface DashboardHeaderProps {
  user?: ChallengeUser;
  walletAddress?: string;
}

export function DashboardHeader({ user, walletAddress }: DashboardHeaderProps) {
  const router = useRouter();

  const handleStartQuests = () => {
    router.push("/quests");
  };

  return (
    <section className="py-4 px-4 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Greeting - Outside container */}
        <h1 className="text-4xl font-bold text-white mb-6">
          Hello, {walletAddress ? `${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}` : 'User'}!
        </h1>
        
        {/* Main Container - Single container with both sides */}
        <div 
          className="rounded-xl p-6 relative overflow-hidden"
          style={{ backgroundColor: '#0C1113' }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Side - Stats */}
            <div className="flex flex-col justify-center">
              {/* Stats Cards - Two rows */}
              <div className="grid grid-cols-2 gap-4">
                {/* Points Card - Bigger */}
                <div className="p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Infinity className="w-4 h-4" style={{ color: '#60FF8A' }} />
                    <h3 className="text-xs font-semibold" style={{ color: '#99A1AF' }}>Points</h3>
                    <div className="relative group">
                      <Info className="w-3 h-3 text-gray-400 cursor-help" />
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                        Earn points by completing quests and referring friends
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-white mb-1">
                    {user?.totalPoints ? parseFloat(user.totalPoints).toLocaleString() : '0'}
                  </div>
                  <div className="text-xs" style={{ color: '#60FF8A' }}>
                    Total Points
                  </div>
                </div>

                {/* Rank Card */}
                <div className="p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Trophy className="w-4 h-4" style={{ color: '#FCC800' }} />
                    <h3 className="text-xs font-semibold" style={{ color: '#99A1AF' }}>Rank</h3>
                    <div className="relative group">
                      <Info className="w-3 h-3 text-gray-400 cursor-help" />
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                        Your position on the global leaderboard
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-white mb-1">
                    {user?.rank ? `#${user.rank.toString()}` : '#--'}
                  </div>
                  <div className="text-xs text-yellow-400">
                    {user?.rank ? `#${user.rank.toString()}` : 'Unranked'}
                  </div>
                </div>

                {/* Items Crafted Card */}
                <div className="p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Hammer className="w-4 h-4" style={{ color: '#C27AFF' }} />
                    <h3 className="text-xs font-semibold" style={{ color: '#99A1AF' }}>Items Crafted</h3>
                    <div className="relative group">
                      <Info className="w-3 h-3 text-gray-400 cursor-help" />
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                        Craft items in Rhuna Forge to earn rewards
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-white mb-1">0</div>
                  <div className="text-xs" style={{ color: '#C27AFF' }}>Items Crafted</div>
                </div>
              </div>
            </div>

            {/* Right Side - Hero Content with border */}
            <div 
              className="lg:col-span-2 rounded-xl p-6 relative overflow-hidden"
              style={{ 
                backgroundColor: '#0C1113',
                backgroundImage: 'url(https://pulsarmoney.b-cdn.net/rhuna/dashboard_header_bg.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                border: '1px solid rgba(55, 65, 81, 0.5)'
              }}
            >
              {/* Enhanced overlay for better contrast */}
              <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/60 to-black/80"></div>
              
              {/* Content */}
              <div className="relative z-10 flex flex-col items-start text-left h-full">
                <div className="flex-1 flex flex-col justify-center">
                  <h2 className="text-2xl font-bold text-white mb-3 drop-shadow-lg">
                    Quests
                  </h2>
                  <p className="text-sm mb-1 max-w-md font-medium text-gray-200">
                  Complete quests to earn points and unlock rewards.                
                  </p>
                  <p className="text-sm mb-1 max-w-md font-medium text-gray-200">
                  Track your progress and climb the leaderboard. 
                  </p>
                  <p className="text-sm mb-6 max-w-md font-medium text-gray-200">
                  Join the Rhuna community today!  
                  </p>
                  <div className="flex justify-start">
                    <Button
                      size="md"
                      className="fboldont-semi px-4 py-2 rounded-lg text-sm transition-colors"
                      style={{ backgroundColor: '#60FF8A', color: '#000000' }}
                      onPress={handleStartQuests}
                    >
                      Start Quests
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}